import { useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

/**
 * Thin wrapper around RQ.useQuery that:
 *   - Accepts a Supabase query-builder thunk (returns `{ data, error }`).
 *   - Throws on Supabase error so RQ retry + error state engage (not empty-success).
 * Ported from index.html:1907 (keep behavior identical during migration).
 */
export function useSupabaseQuery<T = unknown>(
  key: readonly unknown[],
  // Any PostgREST builder: .select/.single/.maybeSingle/.rpc all resolve to {data,error}.
  // We don't constrain the inner type so callers can project via the <T> generic.
  builder: () => Promise<unknown>,
  options?: Omit<UseQueryOptions<T>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<T>({
    queryKey: key,
    queryFn: async () => {
      const res = (await builder()) as { data?: unknown; error?: unknown } | unknown;
      if (res && typeof res === 'object' && 'error' in res && (res as { error: unknown }).error) {
        throw (res as { error: unknown }).error;
      }
      if (res && typeof res === 'object' && 'data' in res) {
        return (res as { data: T }).data;
      }
      return res as T;
    },
    ...(options ?? {}),
  });
}

/**
 * Invalidate any query whose key starts with the given prefix.
 * Call after mutations (form save, delete) to force refetch.
 */
export function useInvalidateSupabase() {
  const qc = useQueryClient();
  return (keyPrefix: readonly unknown[]) => {
    try {
      qc.invalidateQueries({ queryKey: keyPrefix });
    } catch {
      /* no-op */
    }
  };
}
